import React, { useState } from "react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileText, Trash2, CheckCircle2, Loader2, Download } from "lucide-react";
import { downloadUploadFile } from "../lib/api";
import { toast } from "sonner";

function formatBytes(bytes) {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Reusable uploaded-file list with per-row delete + confirmation dialog.
 * Props:
 *  - files: [{id, original_filename, size, ...}]
 *  - onDelete(file) -> Promise
 *  - maxHeight: tailwind class, default "max-h-48"
 *  - testIdPrefix: string to namespace data-testids
 */
export const UploadedFileList = ({
    files = [],
    onDelete,
    maxHeight = "max-h-48",
    testIdPrefix = "uploaded-file",
}) => {
    const [pending, setPending] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);

    const handleDownload = async (file) => {
        setDownloadingId(file.id);
        try {
            await downloadUploadFile(file);
        } catch (err) {
            toast.error(
                `Couldn't download ${file.original_filename || "file"}`,
            );
        } finally {
            setDownloadingId(null);
        }
    };

    const confirm = async () => {
        if (!pending || !onDelete) return;
        setDeleting(true);
        try {
            await onDelete(pending);
            setPending(null);
        } finally {
            setDeleting(false);
        }
    };

    if (!files.length) return null;

    return (
        <>
            <ul
                className={`space-y-1.5 ${maxHeight} overflow-y-auto`}
                data-testid={`${testIdPrefix}-list`}
            >
                {files.map((f) => (
                    <li
                        key={f.id}
                        className="flex items-center gap-2 px-2.5 py-1.5 bg-[#0a0a0a] border border-white/15 rounded-sm min-w-0"
                    >
                        <FileText className="w-4 h-4 text-[#8fa4f0] shrink-0" />
                        <span
                            className="text-xs sm:text-sm text-white flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
                            title={f.original_filename}
                        >
                            {f.original_filename}
                        </span>
                        <span className="text-[10px] sm:text-xs text-slate-500 shrink-0">
                            {formatBytes(f.size)}
                        </span>
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(f);
                            }}
                            disabled={downloadingId === f.id}
                            data-testid={`${testIdPrefix}-download-${f.id}`}
                            aria-label={`Download ${f.original_filename}`}
                            title="Download"
                            className="shrink-0 w-7 h-7 rounded-sm flex items-center justify-center text-slate-400 hover:text-[#8fa4f0] hover:bg-[#1a3fd1]/10 border border-transparent hover:border-[#1a3fd1]/40 transition-colors active:scale-95 disabled:opacity-40 disabled:cursor-wait"
                        >
                            {downloadingId === f.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                                <Download className="w-3.5 h-3.5" />
                            )}
                        </button>
                        {onDelete && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPending(f);
                                }}
                                data-testid={`${testIdPrefix}-delete-${f.id}`}
                                aria-label={`Delete ${f.original_filename}`}
                                className="shrink-0 w-7 h-7 rounded-sm flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 transition-colors active:scale-95"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </li>
                ))}
            </ul>

            <AlertDialog
                open={!!pending}
                onOpenChange={(o) => !o && !deleting && setPending(null)}
            >
                <AlertDialogContent
                    className="bg-[#141414] border border-white/15 text-white w-[calc(100vw-1.5rem)] max-w-[440px]"
                    data-testid={`${testIdPrefix}-delete-confirm`}
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">
                            Delete this file?
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            <span className="text-white font-semibold break-all">
                                {pending?.original_filename}
                            </span>
                            {" "}will be removed from this project. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid={`${testIdPrefix}-cancel-delete`}
                            className="bg-[#1a1a1a] border-white/15 text-slate-300 hover:bg-white/10 hover:text-white"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirm}
                            disabled={deleting}
                            data-testid={`${testIdPrefix}-confirm-delete`}
                            className="bg-red-600 hover:bg-red-500 text-white font-bold"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                </>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default UploadedFileList;
